import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';

interface HubSpotFormEmbedProps {
  portalId: string;
  formId: string;
  region: string;
  /** Called once the embedded form has been successfully submitted (not on button click). */
  onSubmitted?: () => void;
}

/** Shape of the `message` events HubSpot's embed posts to the parent window. */
interface HubSpotFormCallbackMessage {
  type: 'hsFormCallback';
  eventName: string;
  id?: string;
}

function isHubSpotFormCallback(data: unknown): data is HubSpotFormCallbackMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'hsFormCallback' &&
    typeof (data as { eventName?: unknown }).eventName === 'string'
  );
}

export default function HubSpotFormEmbed({ portalId, formId, region, onSubmitted }: HubSpotFormEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // HubSpot renders the form in an iframe and reports lifecycle events to the
  // host page via window.postMessage ({ type: 'hsFormCallback', eventName, id }).
  // 'onFormSubmitted' fires only after the submission was accepted.
  useEffect(() => {
    if (!onSubmitted) return;
    const onMessage = (event: MessageEvent) => {
      const data: unknown = event.data;
      if (!isHubSpotFormCallback(data)) return;
      if (data.eventName !== 'onFormSubmitted') return;
      if (data.id && data.id !== formId) return;
      onSubmitted();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [formId, onSubmitted]);

  useEffect(() => {
    const scriptId = `hs-form-script-${portalId}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    const onReady = () => {
      setLoading(false);
      setError(false);
    };

    const onError = () => {
      setLoading(false);
      setError(true);
    };

    if (script) {
      // Script already loaded from a previous open — form will auto-init via MutationObserver
      onReady();
    } else {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://js.hsforms.net/forms/embed/${portalId}.js`;
      script.defer = true;
      script.addEventListener('load', onReady);
      script.addEventListener('error', onError);
      document.head.appendChild(script);
    }

    return () => {
      script?.removeEventListener('load', onReady);
      script?.removeEventListener('error', onError);
    };
  }, [portalId]);

  const handleRetry = () => {
    setLoading(true);
    setError(false);
    // Remove old script so it re-loads
    const scriptId = `hs-form-script-${portalId}`;
    const old = document.getElementById(scriptId);
    old?.remove();

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://js.hsforms.net/forms/embed/${portalId}.js`;
    script.defer = true;
    script.addEventListener('load', () => { setLoading(false); setError(false); });
    script.addEventListener('error', () => { setLoading(false); setError(true); });
    document.head.appendChild(script);
  };

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            Retry
          </Button>
        }
      >
        Unable to load the form. Please try again.
      </Alert>
    );
  }

  return (
    <Box sx={{ minHeight: 200 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      <div
        ref={containerRef}
        className="hs-form-frame"
        data-region={region}
        data-form-id={formId}
        data-portal-id={portalId}
      />
    </Box>
  );
}
